import { type GraphQLRequest, type GraphQLResponse, postGraphQL } from 'app/utils/hyrox-mirror/graphql';
import type { MirrorAccess, MirrorSessionFailure } from 'app/utils/hyrox-mirror/session';
import { RequestError } from 'app/utils/requests/fetch';

export type AuthenticatedSend<TData> =
  | { ok: true; response: GraphQLResponse<TData> }
  | { ok: false; reason: MirrorSessionFailure };

interface SendAuthenticatedArgs {
  getAccessToken: () => Promise<MirrorAccess>;
  refreshAccessToken: () => Promise<string | null>;
  request: GraphQLRequest;
}

type Attempt<TData> = { authenticated: true; response: GraphQLResponse<TData> } | { authenticated: false };

// hyrox-api rejects a stale token either as a transport 401 or as a 200 carrying the UNAUTHENTICATED
// envelope. `data` must be absent for that to mean "nothing ran": a response carrying both data and a
// field-level UNAUTHENTICATED says the operation did execute, and retrying it would run StartProgressivePlan
// — the one non-idempotent mirrored write — a second time.
const isUnauthenticated = ({ data, errors }: GraphQLResponse<unknown>): boolean =>
  (data === null || data === undefined) &&
  (errors?.some(({ extensions }) => extensions?.code === 'UNAUTHENTICATED') ?? false);

const attempt = async <TData>(request: GraphQLRequest, accessToken: string): Promise<Attempt<TData>> => {
  try {
    const response = await postGraphQL<TData>({ ...request, authorization: `Bearer ${accessToken}` });

    return isUnauthenticated(response) ? { authenticated: false } : { authenticated: true, response };
  } catch (error: unknown) {
    if (error instanceof RequestError && error.status === 401) {
      return { authenticated: false };
    }

    throw error;
  }
};

// One hyrox request with the mirror session: refresh once on a rejected token and retry once, never more.
// Shared by the replay itself and the translation lookups so both fail the same way.
export const sendAuthenticated = async <TData>({
  getAccessToken,
  refreshAccessToken,
  request,
}: SendAuthenticatedArgs): Promise<AuthenticatedSend<TData>> => {
  const access = await getAccessToken();

  if (!access.ok) {
    return { ok: false, reason: access.reason };
  }

  const first = await attempt<TData>(request, access.accessToken);

  if (first.authenticated) {
    return { ok: true, response: first.response };
  }

  const refreshed = await refreshAccessToken();
  const second = refreshed === null ? null : await attempt<TData>(request, refreshed);

  return second?.authenticated
    ? { ok: true, response: second.response }
    : { ok: false, reason: 'mirror_session_expired' };
};
